import { ContractPersonsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/contracts/types/contract-persons.enum';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, notification, Spin } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import { useGetContractPersons } from '@/features/contracts/api/use-get-contract-persons';
import { useUpdateContractPerson } from '@/features/contracts/api/use-update-contract-person';
import { CONTRACT_MODE, useContractModalStore } from '@/store/use-contract-modal-store';
import { validatePersonalCode } from '@/utils/validate-personal-code';

import AttorneyForm, { AttorneyFormValues } from './AttorneyForm';

interface EditAttorneyModalProps {
  isModalOpen: boolean;
  contractPersonId: number;
  contractNumber: string;
}

const EditAttorneyModal = ({ isModalOpen, contractPersonId, contractNumber }: EditAttorneyModalProps) => {
  const { t } = useTranslation('contractAttorneyModal');
  const queryClient = useQueryClient();

  const { setMode } = useContractModalStore();

  const contractPersonsQuery = useGetContractPersons({
    params: {
      ids: [contractPersonId],
      sideloading: [ContractPersonsSideloading.ContractOwner],
      disablePagination: true,
    },
  });

  const updateContractPersonMutation = useUpdateContractPerson({
    mutationConfig: {
      onSuccess: () => {
        notification.success({
          message: t('dataSaved', { ns: 'common' }),
        });
        setMode(CONTRACT_MODE.MANAGE_ATTORNEYS);
      },
    },
  });

  const handleClose = () => {
    setMode(CONTRACT_MODE.MANAGE_ATTORNEYS);
  };

  const handleSubmit = (values: AttorneyFormValues) => {
    const { personalCode, firstname, surname, startDate, endDate, furtherMandateRight } = values;

    if (!validatePersonalCode(personalCode)) {
      notification.error({ message: t('invalidPersonalCode') });
      return;
    }

    if (startDate && endDate && dayjs(endDate).isBefore(startDate, 'day')) {
      notification.error({ message: t('invalidEndDate') });
      return;
    }

    updateContractPersonMutation.mutate(
      {
        id: contractPersonId,
        data: {
          personalCode,
          firstname,
          surname,
          startDate: startDate ? dayjs(startDate).add(1, 'day').toISOString() : null,
          endDate: endDate ? dayjs(endDate).add(1, 'day').toISOString() : null,
          furtherMandateRight,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes('contract-persons'),
          });
        },
        onError: () => {
          notification.error({
            message: t('somethingWentWrong', { ns: 'common' }),
          });
        },
      },
    );
  };

  return (
    <Modal
      centered
      onCancel={handleClose}
      width={600}
      styles={{
        body: {
          height: '60vh',
          overflowY: 'auto',
        },
        content: {
          justifySelf: 'center',
          borderRadius: 24,
          maxWidth: '100%',
          margin: '0 auto',
        },
      }}
      closable
      open={isModalOpen}
      title={
        <div className="flex sm:gap-4 sm:flex-row flex-col sm:items-center">
          <span className="text-[20px] sm:text-[24px] font-bold text-gray-10">{t('edit')}</span>
          <span className="text-[20px] mb-4 sm:mb-0 sm:text-[24px] font-bold text-[#A7AD2A]">L{contractNumber}</span>
        </div>
      }
      footer={
        <div className="flex sm:gap-4 gap-2 sm:flex-row flex-col mt-6">
          <Button
            pending={updateContractPersonMutation.isPending}
            onClick={handleClose}
            variant="secondaryAlt"
            className="flex-1 sm:order-1 order-2"
          >
            {t('close', { ns: 'table' })}
          </Button>
          <Button
            pending={updateContractPersonMutation.isPending}
            form="attorney-form"
            type="submit"
            variant="primary"
            className="flex-1 order-1 sm:order-2"
          >
            {t('save')}
          </Button>
        </div>
      }
    >
      <Spin spinning={contractPersonsQuery.isLoading || updateContractPersonMutation.isPending}>
        <AttorneyForm onSubmit={handleSubmit} contractPerson={contractPersonsQuery.data?.data?.[0]} />
      </Spin>
    </Modal>
  );
};

export default EditAttorneyModal;
